import { CSSTransition } from 'react-transition-group';
import React, { useEffect } from 'react';

import { formatLinkBlockToUrl } from '$utilities/format';
import { useDialog } from '$hooks/dialog';
import Ticker from './ticker.jsx';
import {
    IconClose,
    IconHamburger,
    IconSearch
} from '$partials/icons/index.js';
import { usePage } from '$contexts/page.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Header}
 */
const Header = ({
    ctaCssClass = '',
    ctaLabel = '',
    ctaLink = null,
    ctaMobileUrlOverride = '',
    logo = null,
    navigationColumn1 = '',
    navigationColumn2 = '',
    navigationColumn3 = '',
    pageType = '',
    tickerCtaCssClass = '',
    tickerCtaLabel = '',
    tickerCtaLink = null,
    tickerCtaMobileUrlOverride = '',
    tickerItems = null
}) => {
    const { page } = usePage();

    // hooks
    const {
        dialogRef,
        handleClose,
        handleOpen,
        isOpen,
        scrollRef
    } = useDialog({
        exitDelay: 150
    });

    // Apply PostHog data attributes to rich text navigation links
    useEffect(() => {
        const navigation = document.getElementById('primary-navigation');

        if (navigation) {
            const links = navigation.querySelectorAll('a');

            for (const [i, link] of links.entries()) {
                link.dataset.analyticsAction = 'click';
                link.dataset.analyticsContext = 'navigation';
                link.dataset.analyticsDestination = link.href;
                link.dataset.analyticsId = `${page?.id}_header_link_${i}`;
                link.dataset.analyticsLabel = link.innerText;
                link.dataset.analyticsLocation = 'header';
                link.dataset.analyticsModule = 'header_navigation_link';
            }
        }
    }, []);

    // render
    return (
        <header>
            {/* Ticker*/}
            <Ticker
                tickerCtaCssClass={tickerCtaCssClass}
                tickerCtaLabel={tickerCtaLabel}
                tickerCtaLink={tickerCtaLink}
                tickerCtaMobileUrlOverride={ tickerCtaMobileUrlOverride}
                tickerItems={tickerItems} />

            <div className='layout-container layout-site-outer-padding'>
                <div className='col-wrapper flex flex-wrap'>
                    {/* Left Col */}
                    {/* Logo - Home Page */}
                    {(logo && pageType === 'Home') && (
                        <div className='w-11/12 sm:w-6/12 md:w-7/12 col-padding py-16 md:py-32'>
                            <img
                                className='w-full pointer-events-none select-none'
                                height={logo.height}
                                loading='eager'
                                role='presentation'
                                src={logo.px2}
                                width={logo.width} />
                        </div>
                    )}

                    {/* Logo - Other Page */}
                    {(logo && pageType !== 'Home') && (
                        <div className='w-6/12 col-padding py-16 md:py-32'>
                            <a
                                aria-label='Back to homepage'
                                data-analytics-action='click'
                                data-analytics-context='navigation'
                                data-analytics-destination='/'
                                data-analytics-id={`${page?.id}_header_home`}
                                data-analytics-label='Home'
                                data-analytics-location='header'
                                data-analytics-module='header_navigation_link'
                                href='/'>
                                <img
                                    className='w-full pointer-events-none select-none'
                                    height={logo.height}
                                    loading='eager'
                                    role='presentation'
                                    src={logo.px2}
                                    width={logo.width}/>
                            </a>
                        </div>
                    )}

                    {/* Right Col */}
                    <div className={`col-padding py-16 md:py-32${pageType === 'Home' ? ' w-1/12 sm:w-6/12 md:homepage-logo-offset' : ' w-6/12'}`}>
                        <div className='h-full relative'>
                            <div className='hidden sm:flex h-full flex-col justify-between gap-22'>
                                <div className='self-end flex gap-8'>
                                    {/* Search */}
                                    <a
                                        aria-label='Navigate to search page'
                                        className='button-icon-outcome'
                                        data-analytics-action='click'
                                        data-analytics-context='navigation'
                                        data-analytics-destination='/search'
                                        data-analytics-id={`${page?.id}_header_search`}
                                        data-analytics-label='Search'
                                        data-analytics-location='header'
                                        data-analytics-module='header_navigation_cta'
                                        href='/search'
                                        id='search'>
                                        <IconSearch />
                                    </a>

                                    {/* CTA */}
                                    {(ctaLink && ctaLabel) && (
                                        <div>
                                            {ctaMobileUrlOverride ? (
                                                <>
                                                    <a
                                                        className='sm:hidden button-primary w-full justify-start'
                                                        data-analytics-action='click'
                                                        data-analytics-context='navigation'
                                                        data-analytics-destination={ctaMobileUrlOverride}
                                                        data-analytics-id={`${page?.id}_header_cta`}
                                                        data-analytics-label={ctaLabel}
                                                        data-analytics-location='header'
                                                        data-analytics-module='header_navigation_cta'
                                                        href={ctaMobileUrlOverride}
                                                        rel='noreferrer'
                                                        target='_blank'>
                                                        {ctaLabel}
                                                    </a>
                                                    <a
                                                        className={`hidden sm:inline-block button-primary w-full justify-start ${ctaCssClass ? ' ' + ctaCssClass: ''}`}
                                                        data-analytics-action='click'
                                                        data-analytics-context='navigation'
                                                        data-analytics-destination={ctaMobileUrlOverride}
                                                        data-analytics-id={`${page?.id}_header_cta`}
                                                        data-analytics-label={ctaLabel}
                                                        data-analytics-location='header'
                                                        data-analytics-module='header_navigation_cta'
                                                        href={formatLinkBlockToUrl(ctaLink)}
                                                        rel='noreferrer'
                                                        target='_blank'>
                                                        {ctaLabel}
                                                    </a>
                                                </>
                                            ) : (
                                                <a
                                                    className={`button-primary w-full justify-start ${ctaCssClass ? ' ' + ctaCssClass: ''}`}
                                                    data-analytics-action='click'
                                                    data-analytics-context='navigation'
                                                    data-analytics-destination={formatLinkBlockToUrl(ctaLink)}
                                                    data-analytics-id={`${page?.id}_header_cta`}
                                                    data-analytics-label={ctaLabel}
                                                    data-analytics-location='header'
                                                    data-analytics-module='header_navigation_cta'
                                                    href={formatLinkBlockToUrl(ctaLink)}
                                                    rel='noreferrer'
                                                    target='_blank'>
                                                    {ctaLabel}
                                                </a>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Navigation */}
                                <div>
                                    <nav
                                        className='rich-text-header col-wrapper flex'
                                        id='primary-navigation'>
                                        {navigationColumn1 && (
                                            <div
                                                className='w-2/6 col-padding flex flex-col type-style-sans-body'
                                                dangerouslySetInnerHTML={{ __html: navigationColumn1 }} />
                                        )}

                                        {navigationColumn2 && (
                                            <div
                                                className='w-2/6 col-padding flex flex-col type-style-sans-body'
                                                dangerouslySetInnerHTML={{ __html: navigationColumn2 }} />
                                        )}

                                        {navigationColumn3 && (
                                            <div
                                                className='w-2/6 col-padding flex flex-col type-style-sans-body'
                                                dangerouslySetInnerHTML={{ __html: navigationColumn3 }} />
                                        )}
                                    </nav>
                                </div>
                            </div>

                            {/* Menu Button */}
                            <button
                                aria-label='Toggle mobile menu'
                                className='sm:hidden absolute top-0 right-0 -mr-10 p-10 flex items-center justify-center'
                                onClick={handleOpen}
                                type='button'>
                                <IconHamburger />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Menu - Mobile */}
            <CSSTransition
                classNames='fade-in'
                in={isOpen}
                nodeRef={dialogRef}
                timeout={150}>
                <dialog
                    className='dialog-reset backdrop:bg-white transition-opacity duration-[150ms] ease-in-out'
                    onClick={(e) => (e?.target === dialogRef?.current) && handleClose() }
                    ref={dialogRef}>
                    <div
                        className='h-full py-16'
                        ref={scrollRef}>
                        <div className='h-full layout-container layout-site-outer-padding'>
                            <div className='h-full col-wrapper'>
                                <div className='h-full w-full col-padding flex flex-col justify-between'>
                                    <div className='relative'>
                                        {/* Logo */}
                                        <div>
                                            <div className='w-6/12 pb-24'>
                                                {(logo && pageType === 'Home') && (
                                                    <img
                                                        className='w-full pointer-events-none select-none'
                                                        height={logo.height}
                                                        loading='eager'
                                                        role='presentation'
                                                        src={logo.px2}
                                                        width={logo.width} />
                                                )}

                                                {(logo && pageType !== 'Home') && (
                                                    <a
                                                        aria-label='Back to homepage'
                                                        data-analytics-action='click'
                                                        data-analytics-context='navigation'
                                                        data-analytics-destination='/'
                                                        data-analytics-id={`${page?.id}_header_home`}
                                                        data-analytics-label='Home'
                                                        data-analytics-location='header'
                                                        data-analytics-module='header_navigation_link'
                                                        href='/'>
                                                        <img
                                                            className='w-full pointer-events-none select-none'
                                                            height={logo.height}
                                                            loading='eager'
                                                            role='presentation'
                                                            src={logo.px2}
                                                            width={logo.width} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>

                                        {/* Navigation */}
                                        <div>
                                            <nav
                                                className='rich-text-header flex flex-col'
                                                id='primary-navigation'>
                                                <div
                                                    className='contents type-style-sans-body'
                                                    dangerouslySetInnerHTML={{ __html: navigationColumn1 + navigationColumn2 + navigationColumn3}} />
                                            </nav>
                                        </div>

                                        {/* Close Menu Button */}
                                        <button
                                            aria-label='Close mobile menu'
                                            className='absolute top-0 right-0 -mr-10 p-10 flex items-center justify-center'
                                            onClick={handleClose}
                                            type='button'>
                                            <IconClose
                                                height={30}
                                                width={30} />
                                        </button>
                                    </div>

                                    <div className='flex gap-8 pb-24'>
                                        {/* Search */}
                                        <a
                                            aria-label='Navigate to search page'
                                            className='button-icon-outcome'
                                            data-analytics-action='click'
                                            data-analytics-context='navigation'
                                            data-analytics-destination='/search'
                                            data-analytics-id={`${page?.id}_header_search`}
                                            data-analytics-label='Search'
                                            data-analytics-location='header'
                                            data-analytics-module='header_navigation_cta'
                                            href='/search'
                                            id='search'>
                                            <IconSearch />
                                        </a>

                                        {/* CTA */}
                                        {(ctaLink && ctaLabel) && (
                                            <div className='w-full'>
                                                {ctaMobileUrlOverride ? (
                                                    <>
                                                        <a
                                                            className='sm:hidden button-primary w-full justify-start'
                                                            data-analytics-action='click'
                                                            data-analytics-context='navigation'
                                                            data-analytics-destination={ctaMobileUrlOverride}
                                                            data-analytics-id={`${page?.id}_header_cta`}
                                                            data-analytics-label={ctaLabel}
                                                            data-analytics-location='header'
                                                            data-analytics-module='header_navigation_cta'
                                                            href={ctaMobileUrlOverride}
                                                            rel='noreferrer'
                                                            target='_blank'>
                                                            {ctaLabel}
                                                        </a>
                                                        <a
                                                            className={`hidden sm:inline-block button-primary w-full justify-start ${ctaCssClass ? ' ' + ctaCssClass: ''}`}
                                                            data-analytics-action='click'
                                                            data-analytics-context='navigation'
                                                            data-analytics-destination={ctaMobileUrlOverride}
                                                            data-analytics-id={`${page?.id}_header_cta`}
                                                            data-analytics-label={ctaLabel}
                                                            data-analytics-location='header'
                                                            data-analytics-module='header_navigation_cta'
                                                            href={formatLinkBlockToUrl(ctaLink)}
                                                            rel='noreferrer'
                                                            target='_blank'>
                                                            {ctaLabel}
                                                        </a>
                                                    </>
                                                ) : (
                                                    <a
                                                        className={`button-primary w-full justify-start ${ctaCssClass ? ' ' + ctaCssClass: ''}`}
                                                        data-analytics-action='click'
                                                        data-analytics-context='navigation'
                                                        data-analytics-destination={formatLinkBlockToUrl(ctaLink)}
                                                        data-analytics-id={`${page?.id}_header_cta`}
                                                        data-analytics-label={ctaLabel}
                                                        data-analytics-location='header'
                                                        data-analytics-module='header_navigation_cta'
                                                        href={formatLinkBlockToUrl(ctaLink)}
                                                        rel='noreferrer'
                                                        target='_blank'>
                                                        {ctaLabel}
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </dialog>
            </CSSTransition>
        </header>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default Header;
